import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff } from 'lucide-react';
import { Card, CardContent } from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import { useApp } from '../../context/AppContext';
import { getBrokerConfig, getBrokerFromPath } from '../../config/brokerConfig';
import './AdminLogin.css';

function AdminLogin() {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAdminAuthenticated, loginAdmin } = useApp();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  // Get broker config based on URL path
  const brokerId = getBrokerFromPath(location.pathname);
  const brokerConfig = getBrokerConfig(brokerId);

  // Redirect if already authenticated
  useEffect(() => {
    if (isAdminAuthenticated) {
      navigate(`/${brokerId}/admin/dashboard`);
    }
  }, [isAdminAuthenticated, navigate, brokerId]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setErrors({});

    // Validation
    const newErrors = {};
    if (!email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(email)) {
      newErrors.email = 'Please enter a valid email';
    }
    if (!password) {
      newErrors.password = 'Password is required';
    }

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return;
    }

    setIsLoading(true);

    // Authenticate admin
    setTimeout(() => {
      if (email === 'admin@anbpay.com' && password === 'admin123') {
        loginAdmin({
          id: 'ADMIN001',
          name: 'Admin User',
          email: email,
          role: 'admin',
          broker: brokerId,
        });
        navigate(`/${brokerId}/admin/dashboard`);
      } else {
        setErrors({ general: 'Invalid email or password' });
      }
      setIsLoading(false);
    }, 1000);
  };

  return (
    <div className="admin-login" style={{ background: brokerConfig.theme.headerGradient }}>
      <div className="login-container">
        <div className="login-branding">
          <div className="logo">
            <img src={brokerConfig.logo} alt={brokerConfig.name} className="login-savexa-logo" />
          </div>
          <h1>{brokerConfig.name}</h1>
          <p>Admin Portal</p>
        </div>

        <Card variant="elevated" className="login-card">
          <CardContent>
            <h2>Sign In</h2>
            <p className="login-subtitle">Enter your credentials to access the admin portal</p>

            {errors.general && (
              <div className="error-banner">
                {errors.general}
              </div>
            )}

            <form onSubmit={handleSubmit} className="login-form">
              <Input
                label="Email Address"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                icon={Mail}
                error={errors.email}
                required
              />

              <div className="password-field">
                <Input
                  label="Password"
                  type={showPassword ? 'text' : 'password'}
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  icon={Lock}
                  error={errors.password}
                  required
                />
                <button
                  type="button"
                  className="toggle-password"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                </button>
              </div>

              <div className="login-options">
                <label className="remember-me">
                  <input type="checkbox" />
                  <span>Remember me</span>
                </label>
                <a href="#forgot" className="forgot-link">Forgot password?</a>
              </div>

              <Button
                type="submit"
                variant="primary"
                size="large"
                fullWidth
                loading={isLoading}
                style={{
                  background: `linear-gradient(135deg, ${brokerConfig.primaryColor} 0%, ${brokerConfig.secondaryColor} 100%)`,
                  borderColor: brokerConfig.primaryColor
                }}
              >
                Sign In
              </Button>
            </form>

          </CardContent>
        </Card>

        <p className="footer-text">
          &copy; 2024 {brokerConfig.name}. All rights reserved.
        </p>
      </div>
    </div>
  );
}

export default AdminLogin;
